// =============================================================================
// Origin UI — src/hooks/useModuleFlags.ts
// Fetches module flags from the API on app load.
// Caches the result. Defaults to hiding modules until response arrives.
// Now reads a dynamic dictionary from the DB-backed endpoint.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { api } from '@/services/apiService';

type ModuleFlags = Record<string, boolean>;

let cachedFlags: ModuleFlags | null = null;
let fetchPromise: Promise<ModuleFlags> | null = null;

async function fetchFlags(): Promise<ModuleFlags> {
  if (cachedFlags) return cachedFlags;
  if (fetchPromise) return fetchPromise;

  fetchPromise = api.get<ModuleFlags>('/api/config/modules')
    .then(flags => {
      cachedFlags = flags;
      return flags;
    })
    .catch(() => {
      // Default to all enabled if fetch fails
      const defaults: ModuleFlags = {};
      cachedFlags = defaults;
      return defaults;
    });

  return fetchPromise;
}

/** Call this to force a re-fetch (e.g. after admin toggles a module) */
export function invalidateModuleFlags() {
  cachedFlags = null;
  fetchPromise = null;
}

export function useModuleFlags() {
  const [flags, setFlags] = useState<ModuleFlags>(cachedFlags || {});
  const [loaded, setLoaded] = useState(!!cachedFlags);

  useEffect(() => {
    if (cachedFlags) {
      setFlags(cachedFlags);
      setLoaded(true);
      return;
    }
    fetchFlags().then(f => {
      setFlags(f);
      setLoaded(true);
    });
  }, []);

  const isModuleEnabled = useCallback((moduleName: string): boolean => {
    if (!loaded) return false;
    // Look up camelCase key in the flags dictionary
    const key = moduleName.charAt(0).toLowerCase() + moduleName.slice(1);
    // If the key doesn't exist in the dictionary, default to enabled
    if (!(key in flags)) return true;
    return flags[key];
  }, [flags, loaded]);

  const refresh = useCallback(() => {
    invalidateModuleFlags();
    fetchFlags().then(f => {
      setFlags(f);
      setLoaded(true);
    });
  }, []);

  return { flags, loaded, isModuleEnabled, refresh };
}
